import { useContext, useEffect, useReducer, useState } from "react";
import JourneyTable from "./JourneyTable";
import useAuthFetch from "../../hooks/useAuthFetch";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AuthContext from "../../context/AuthContext";
import { formatDate } from "../../utils/common";
import { format } from "date-fns";


export default function AllJourneys() {
  const [query, setQuery] = useState({});
  const [isReady, setIsReady] = useState(false);
  const { user } = useContext(AuthContext);
  const customFetch = useAuthFetch();
  const location = useLocation();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false)
  const [object, setObject] = useState([
    {
      label: "Application Number",
      searchFilter: "applicationNo",
      value: null,
    },
    {
      label: "Agent Code",
      searchFilter: "agentCode",
      value: null,
    },
    {
      searchFilter: "start",
      value: null,
    },
    {
      searchFilter: "end",
      value: null,
    },
    {
      label: "Status",
      searchFilter: "status",
      value: null,
    },
    {
      // Value is an operator + threshold encoded as one string (">=80"), or the
      // literal "unscored". Kept as a single value so it flows through the
      // existing chip / URL-sync / reset / export pipeline unchanged.
      label: "Verification Score",
      searchFilter: "verificationScore",
      value: null,
    },
    // {
    //   label : "withTextMatching",
    //   searchFilter : "withTextMatching",
    //   value: null
    // }
  ]);

  useEffect(() => {
    if (user && location?.state?.showToast) {
      toast.success(
        `Last Successful Login: ${user?.lastSuccessfulLogin
          ? formatDate(user?.lastSuccessfulLogin)
          : "N/A"
        } \n Last Failed Login: ${user?.lastFailedLogin ? formatDate(user?.lastFailedLogin) : "N/A"
        }`,
        {
          position: "bottom-right",
          style: {
            maxWidth: "500px",
            whiteSpace: "nowrap",
          },
        }
      );
    }
  }, []);

  function reducer(state, action) {
    switch (action.type) {
      case "LOADING":
        return { ...state, isLoading: true };

      case "UPDATE":
        return { ...state, response: action.payload, isLoading: false };

      case "ERROR":
        return { ...state, response: null, isLoading: false };

      default:
        throw new Error();
    }
  }

  const [state, dispatch] = useReducer(reducer, {
    response: null,
    isLoading: false,
  });

  useEffect(() => {
    const simulatedQuery = {};
    object.forEach((item) => {
      if (item.value !== null) {
        if (item.searchFilter === "start" || item.searchFilter === "end") {
          simulatedQuery[item.searchFilter] = new Date(
            item.value
          ).toISOString();
        } else {
          simulatedQuery[item.searchFilter] = item.value;
        }
      }
    });

    // Parse existing query params from the URL
    const searchParams = new URLSearchParams(location.search);
    const existingQuery = Object.fromEntries(searchParams.entries());

    // Remove keys from `existingQuery` that have a `null` value in `object`
    Object.keys(existingQuery).forEach((key) => {
      const correspondingObjectItem = object.find(
        (item) => item.searchFilter === key
      );
      if (correspondingObjectItem && correspondingObjectItem.value === null) {
        delete existingQuery[key];
      }
    });

    // Combine `existingQuery` with `simulatedQuery` and add `page=1`
    const newQuery = {
      ...existingQuery,
      ...simulatedQuery,
      page: 1, // Reset page to 1
    };

    // Filter out null or undefined values
    const filteredQuery = Object.fromEntries(
      Object.entries(newQuery).filter(
        ([_, value]) => value !== null && value !== undefined
      )
    );

    // Update the state and URL
    setQuery(filteredQuery);

    const newSearch = new URLSearchParams(filteredQuery).toString();
    if (location.search !== `?${newSearch}`) {
      navigate(`${location.pathname}?${newSearch}`, { replace: true });
    }
    setIsReady(true);
  }, [object]);

  const handleFetch = async (fromFilter = false) => {
    if (!isReady || state.isLoading) return;

    fromFilter ? setIsExporting(true) : dispatch({ type: "LOADING" });

    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/journey/fetchAll`
    );

    url.search = new URLSearchParams({
      ...query,
      page: query.page || 1,
      perPage: fromFilter ? 1000 : query.perPage || 20,
      fromFilter: fromFilter
    }).toString();

    try {
      const method = "GET"
      const { response, status } = await customFetch(
        url,
        method,
      );

      //Export to csv logic
      if (fromFilter && status === 200) {
        const json = response;
        const data = json.data;

        if (!data || data.length === 0) return;

        const formattedRows = data.map((row) => {
          const newRow = { ...row };

          newRow.dateOfBirth = format(new Date(row.dateOfBirth), "dd MMM yyyy");
          newRow.startTime = format(new Date(row.startTime), "dd MMM yyyy, hh:mm a");
          newRow.endTime = format(new Date(row.endTime), "dd MMM yyyy, hh:mm a");

          Object.keys(newRow).forEach((key) => {
            if (typeof newRow[key] === "object" && newRow[key] !== null) {
              newRow[key] = JSON.stringify(newRow[key]);
            }
          });

          return newRow;
        });

        const headers = Object.keys(formattedRows[0]).join(",");
        const rows = formattedRows.map((row) =>
          Object.values(row)
            .map((value) => `"${String(value).replace(/"/g, '""')}"`)
            .join(",")
        );

        const csv = [headers, ...rows].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", "journeys.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsExporting(false)
        return;
      }

      if (status === 200) {
        const data = response;
        dispatch({ type: "UPDATE", payload: data });
      } else {
        dispatch({ type: "ERROR" });
      }
    } catch (error) {
      console.error(error);
      dispatch({ type: "ERROR" });
    }
  };

  const handleFetchWithPage = async(page) => {
    const updatedQuery = { ...query, page }; // Merge current query with the new page
    const searchParams = new URLSearchParams(updatedQuery).toString();

    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/journey/fetchAll`
    );

    url.search = searchParams;

    dispatch({ type: "LOADING" });

    try {
      const method = "GET"
      const { response, status } = await customFetch(
        url,
        method,
      );
      if (status === 200) {
        dispatch({ type: "UPDATE", payload: response });
      } else {
        dispatch({ type: "ERROR" });
      }
    } catch (err) {
      console.error("Fetch error:", err);
      dispatch({ type: "ERROR" });
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const updatedQuery = Object.fromEntries(searchParams.entries());

    setQuery(updatedQuery);

    // Fetch the new data based on updated query
    if (isReady) handleFetch();
  }, [location.search, isReady]);

  return (
    <div className="overflow-y-hidden">
      {isReady && (
        <JourneyTable
          reqState={state}
          object={object}
          setObject={setObject}
          handleFetch={handleFetch}
          handleFetchWithPage={handleFetchWithPage}
          isExporting={isExporting}
        />
      )}
    </div>
  );
}
